import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate } from "src/utils/dateTime";
import { DatePickerField, fromISODate, toUTCISODate } from "src/view/conponents/DateRangePicker";
import { Box, HBox, PanelBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import { IconCalendar } from "@ui/icons/IconCalendar";
import { IconEdit } from "@ui/icons/IconEdit";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Typo } from "@ui/text/Typo";
import { Fragment, useEffect, useState } from "react";
import { GD } from "src/GD";
import { FormParser } from "./FormParser";

interface ILimit {
	start_date: number;
	currencies: {
		currency: string;
		global_limits: {
			day_limit: number;
			month_limit: number;
			day_transactions_limit: number;
			month_transactions_limit: number;
		};
		user_limits: {
			day_limit_per_user: number;
			month_limit_per_user: number;
			day_transactions_limit_per_user: number;
			month_transactions_limit_per_user: number;
		};
		allowed_countries: {
			alpha2: string;
			name: string;
		}[];
	}[];
}

export const Limits = (req: { target_type: string; target_id: number; type?: string }) => {
	useLocale();
	let { type } = req;
	if (!type) type = "limits";
	const { target_type, target_id } = req;
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<IError | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [_updater, setUpdater] = useState(0);
	const [editingDateIndex, setEditingDateIndex] = useState<number | null>(null);

	useEffect(() => {
		loadData();
	}, [req]);

	const loadData = async () => {
		setBusy(true);
		const result = await GD.REQ_API.request({
			method: "settings/get",
			data: {
				target_type,
				target_id,
				type: type,
			},
		});
		if ("error" in result && result.error) {
			setError(result.error);
		} else {
			setData(result.data);
			console.log(result.data);
		}
		setBusy(false);
	};

	const updateLocalData = () => {
		setUpdater(prev => prev + 1);
	};

	const addNewRecord = () => {
		let maxMs = 0;

		data.data.limits.forEach((l: ILimit) => {
			if (!l.start_date) return;

			let ms: number;
			if (typeof l.start_date === "string") {
				const parsed = Number(l.start_date);
				ms = !isNaN(parsed) ? parsed : Date.parse(l.start_date);
			} else {
				ms = l.start_date;
			}

			if ((ms + "").length === 10) ms *= 1000;

			if (ms > maxMs) maxMs = ms;
		});

		const nextDate = new Date();

		if (maxMs > 0) {
			const lastDate = new Date(maxMs);
			const today = new Date();

			today.setHours(0, 0, 0, 0);
			lastDate.setHours(0, 0, 0, 0);

			if (lastDate >= today) {
				nextDate.setTime(lastDate.getTime());
				nextDate.setDate(nextDate.getDate() + 1);
			}
		}

		const newLimit = JSON.parse(JSON.stringify(data.data.limits[0] || {}));
		nextDate.setHours(0, 0, 0, 0);
		newLimit.start_date = nextDate.getTime();

		data.data.limits.push(newLimit);

		updateLocalData();
	};

	const saveChanges = async () => {
		console.log(data);

		if (busy) return;
		setBusy(true);
		const result = await GD.REQ_API.request({
			method: "settings/set",
			data: data,
		});
		if ("error" in result && result.error) {
			setBusy(false);
			setError(result.error);
			return;
		}
		loadData();
	};

	const addNewCurrency = (ref: any, placeholder: any) => {
		// get available currency

		const availableCurrencies = ref["$__currency_values"];
		const addedCurrencies = placeholder.map((c: any) => c.currency);
		const leftCurrencies = availableCurrencies.filter((c: string) => !addedCurrencies.includes(c));
		if (leftCurrencies.length === 0) return;

		const newCurrency = JSON.parse(JSON.stringify(ref));
		newCurrency.currency = leftCurrencies[0];
		placeholder.push(newCurrency);
		updateLocalData();
	};

	const initialClientRecord = () => {
		const record: any[] = data.data.fees;
		record[0].start_date = Date.now();
		saveChanges();
	};
	const initiateRecord = async () => {
		if (busy) return;
		if (type === "client_fees") {
			initialClientRecord();
			return;
		}
		const limit: ILimit[] = data.data.limits;
		limit[0].start_date = Date.now();
		limit[0].currencies[0].currency = (limit[0].currencies[0] as any)["$__currency_values"][0];
		saveChanges();
	};

	if (!data) return null;

	const form = type === "client_fees" ? data.data.fees : data.data.limits;
	console.log(form);

	let noData = data.created_at === 0;

	return (
		<>
			<Error error={error} />
			<ProgressBar busy={busy} />

			{noData && (
				<PanelBox variant="no-records">
					<Typo variant="medium" bold>{msg("noLimitsDefinedYetPleaseAddNewRecordToSetUpLimits")}</Typo>
					<Spacer variant="normal" />
					<Button disabled={busy} variant="primarySmall" onClick={() => initiateRecord()}>
						{busy ? msg("adding") : msg("addNewRecord")}
					</Button>
				</PanelBox>
			)}

			{!noData &&
				form &&
				form.map((item: ILimit, index: number) => (
					<Fragment key={item.start_date + "_" + index}>
						{/* LIMIT DATE */}
						<PanelBox variant="strict">
							<HBox variant="left-center">
								<IconCalendar />
								<Typo variant="medium">{msg("limitsSetupValidFrom")}</Typo>
								<Typo variant="medium" bold>
									<DateTimeTooltip value={item.start_date}>{formatDisplayDate(item.start_date, { locale: "en-US", format: { weekday: "short", month: "short", day: "2-digit", year: "numeric" } })}</DateTimeTooltip>
								</Typo>
								{(() => {
									const startDate = new Date(item.start_date);
									startDate.setHours(0, 0, 0, 0);
									const today = new Date();
									today.setHours(0, 0, 0, 0);
									return startDate >= today;
								})() && (
									<>
										{/* <Spacer variant="small" /> */}
										{editingDateIndex === index ? (
											<DatePickerField
												autoOpen
												style={{ width: "170px" }}
												min={new Date().toISOString().split("T")[0]}
												value={toUTCISODate(new Date(item.start_date))}
												onChange={val => {
													const newDate = fromISODate(val);
													if (!newDate) return;
													item.start_date = newDate.getTime();
													setEditingDateIndex(null);
													updateLocalData();
												}}
												onDismiss={() => setEditingDateIndex(null)}
											/>
										) : (
											<Box
												onClick={() => setEditingDateIndex(index)}
												sx={{
													cursor: "pointer",
													display: "flex",
													alignItems: "center",
													opacity: 0.6,
													"&:hover": { opacity: 1 },
												}}>
												<IconEdit color="#505050" width={20} height={20} />
											</Box>
										)}
									</>
								)}
							</HBox>
						</PanelBox>
						<Spacer variant="tiny" />

						<HBox>
							<Spacer variant="normal" />
							<VBox style={{ gap: "5px", flexGrow: 1 }}>
								{item.currencies.map((c: any, i: number) => (
									<VBox style={{ flexGrow: 1 }} key={item.start_date + "_" + c.currency + "_" + i}>
										<PanelBox variant="light">
											<HBox variant="left-center">
												<Typo variant="medium" bold>{msg("currency_9fc8cc2a")}{c.currency}
												</Typo>

												<Spacer grow />
												<Button
													variant="secondarySmall"
													onClick={() => {
														addNewCurrency(c, item.currencies);
													}}>{msg("currency_9c0ab5ae")}</Button>
											</HBox>
										</PanelBox>
										<Spacer variant="tiny" />
										<HBox>
											<Spacer variant="normal" />

											<Box style={{ flexGrow: 1 }}>
												<FormParser
													grow
													key={"global_limits_" + c.currency}
													size="small"
													primaryButton={null}
													secondaryButton={null}
													onSubmit={async (_data: unknown): Promise<any> => {
														return data;
													}}
													onRequest={async (): Promise<unknown> => {
														const obj = setupObject("global_limits", c);
														const keys = Object.keys(obj["global_limits"]);
														const tobj = obj["global_limits"];
														for (let key of keys) {
															console.log(key);
															if (tobj["$__" + key + "_type"] === "amount") {
																tobj["$__" + key + "_units"] = c.currency;
															}
														}
														return { data: obj };
													}}
												/>
												<Spacer variant="tiny" />
												<FormParser
													grow
													key={"user_limits_" + c.currency}
													size="small"
													primaryButton={null}
													secondaryButton={null}
													//previewMode
													onSubmit={async (data: unknown): Promise<any> => {
														return data;
													}}
													onRequest={async (): Promise<unknown> => {
														const obj = setupObject("user_limits", c);
														const keys = Object.keys(obj["user_limits"]);
														const tobj = obj["user_limits"];
														for (let key of keys) {
															if (tobj["$__" + key + "_type"] === "amount") {
																tobj["$__" + key + "_units"] = c.currency;
															}
														}
														return { data: obj };
													}}
												/>
												<Spacer variant="tiny" />
												<FormParser
													grow
													key={"allowed_countries_" + c.currency}
													size="small"
													primaryButton={null}
													secondaryButton={null}
													//previewMode
													onSubmit={async (data: unknown): Promise<any> => {
														return data;
													}}
													onRequest={async (): Promise<unknown> => {
														return { data: setupObject("allowed_countries", c) };
													}}
												/>
											</Box>
										</HBox>
									</VBox>
								))}
							</VBox>
						</HBox>
						<Spacer />
					</Fragment>
				))}

			{!noData && (
				<>
					<Spacer />
					<HBox>
						<Spacer grow />
						<Button variant="secondarySmall" onClick={() => addNewRecord()}>{msg("addNewRecord")}</Button>
						<Spacer variant="small" />
						<Button variant="primarySmall" onClick={() => saveChanges()}>
							{busy ? msg("saving") : msg("saveChanges")}
						</Button>
					</HBox>
				</>
			)}
		</>
	);
};

const setupObject = (name: string, data: any) => {
	const obj: any = {};
	const keys = Object.keys(data);
	for (let key of keys) {
		if (key === name) obj[key] = data[key];
		if (key.startsWith("$__" + name + "_")) obj[key] = data[key];
	}
	return obj;
};
